import type { AxiosError as ApiError } from "axios"
import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Table,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi"

import {
  BudgetCategorys,
  BudgetCategoryTypes,
  CoContributionDefaults,
  ServiceCatalog,
  type BudgetCategoryPublic,
  type BudgetCategoryTypePublic,
  type BudgetSubCategoryPublic,
  } from "@/client"
import useAuth from "@/hooks/useAuth"
import { handleError } from "@/utils"

const PENSION_TYPES = ["Full Pension", "Part Pension", "Self Funded"]
const PERIOD_OPTIONS = ["Weekly", "Fortnightly", "Monthly", "Quarterly", "Yearly"]

import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export const Route = createFileRoute("/_layout/provider/budgetcategories")({
  component: BudgetCategoriesPage,
})

const selectStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  fontSize: "0.875rem",
  width: "100%",
}

// ── Funding Types ─────────────────────────────────────────────────────────────

const TypeDialog = ({
  existing,
  trigger,
  isSuperuser,
}: {
  existing?: BudgetCategoryTypePublic
  trigger: React.ReactNode
  isSuperuser: boolean
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      categoryType: existing?.categoryType ?? "",
      isGlobal: existing?.isGlobal ?? false,
      budgetPeriodType: existing?.budgetPeriodType ?? "",
      carryOverPercent: existing?.carryOverPercent ?? "",
      carryOverAmount: existing?.carryOverAmount ?? "",
      isCarryOverGreater: existing?.isCarryOverGreater ?? false,
      isReoccuring: existing?.isReoccuring ?? false,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: object) =>
      existing
        ? BudgetCategoryTypes.updateBudgetCategoryType({ path: { id: existing.id }, body: data as never })
        : BudgetCategoryTypes.createBudgetCategoryType({ body: data as never }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetCategoryTypes"] })
      setOpen(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  const onSubmit = (raw: {
    categoryType: string
    isGlobal: boolean
    budgetPeriodType: string
    carryOverPercent: string | number
    carryOverAmount: string | number
    isCarryOverGreater: boolean
    isReoccuring: boolean
  }) => {
    mutation.mutate({
      categoryType: raw.categoryType || null,
      isGlobal: raw.isGlobal,
      budgetPeriodType: raw.budgetPeriodType || null,
      carryOverPercent: raw.carryOverPercent !== "" ? Number(raw.carryOverPercent) : null,
      carryOverAmount: raw.carryOverAmount !== "" ? Number(raw.carryOverAmount) : null,
      isCarryOverGreater: raw.isCarryOverGreater,
      isReoccuring: raw.isReoccuring,
    })
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={({ open: o }) => {
        setOpen(o)
        if (o) reset({
          categoryType: existing?.categoryType ?? "",
          isGlobal: existing?.isGlobal ?? false,
          budgetPeriodType: existing?.budgetPeriodType ?? "",
          carryOverPercent: existing?.carryOverPercent ?? "",
          carryOverAmount: existing?.carryOverAmount ?? "",
          isCarryOverGreater: existing?.isCarryOverGreater ?? false,
          isReoccuring: existing?.isReoccuring ?? false,
        })
      }}
      size={{ base: "xs", md: "md" }}
      placement="center"
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{existing ? "Edit" : "Add"} Funding Type</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody display="flex" flexDir="column" gap={3}>
            <Field.Root>
              <Field.Label fontSize="sm">Name</Field.Label>
              <Input size="sm" {...register("categoryType", { required: true })} placeholder="e.g. NDIS, HCP" />
            </Field.Root>
            {isSuperuser && (
              <Checkbox.Root defaultChecked={existing?.isGlobal ?? false}>
                <Checkbox.HiddenInput {...register("isGlobal")} />
                <Checkbox.Control />
                <Checkbox.Label fontSize="sm">Global (applies to all providers)</Checkbox.Label>
              </Checkbox.Root>
            )}
            <Field.Root>
              <Field.Label fontSize="sm">Budget Period</Field.Label>
              <select style={selectStyle} {...register("budgetPeriodType")}>
                <option value="">— Select —</option>
                {PERIOD_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field.Root>
            <Flex gap={3}>
              <Field.Root flex={1}>
                <Field.Label fontSize="sm">Carry Over %</Field.Label>
                <Input size="sm" type="number" step={0.01} min={0} max={100} {...register("carryOverPercent")} />
              </Field.Root>
              <Field.Root flex={1}>
                <Field.Label fontSize="sm">Carry Over Amount $</Field.Label>
                <Input size="sm" type="number" step={0.01} min={0} {...register("carryOverAmount")} />
              </Field.Root>
            </Flex>
            <Flex gap={4}>
              <Checkbox.Root defaultChecked={existing?.isCarryOverGreater ?? false}>
                <Checkbox.HiddenInput {...register("isCarryOverGreater")} />
                <Checkbox.Control />
                <Checkbox.Label fontSize="sm">Use whichever carry-over is greater</Checkbox.Label>
              </Checkbox.Root>
              <Checkbox.Root defaultChecked={existing?.isReoccuring ?? false}>
                <Checkbox.HiddenInput {...register("isReoccuring")} />
                <Checkbox.Control />
                <Checkbox.Label fontSize="sm">Recurring</Checkbox.Label>
              </Checkbox.Root>
            </Flex>
          </DialogBody>
          <DialogFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} type="button">Cancel</Button>
            <Button size="sm" colorPalette="blue" type="submit" loading={mutation.isPending}>Save</Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

const BudgetCategoryTypesTab = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false

  const { data, isLoading } = useQuery({
    queryKey: ["budgetCategoryTypes"],
    queryFn: () => BudgetCategoryTypes.listBudgetCategoryTypes({ query: { limit: 200 } }),
  })
  const types = data?.data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => BudgetCategoryTypes.deleteBudgetCategoryType({ path: { id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["budgetCategoryTypes"] }),
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.500">Funding types such as NDIS, HCP, CHSP — each with their own budget period and carry-over rules.</Text>
        <TypeDialog
          isSuperuser={isSuperuser}
          trigger={<Button size="sm" colorPalette="blue"><FiPlus /> Add Funding Type</Button>}
        />
      </Flex>
      {isLoading && <Spinner size="sm" />}
      {!isLoading && (
        <Table.Root size="sm" variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Scope</Table.ColumnHeader>
              <Table.ColumnHeader>Period</Table.ColumnHeader>
              <Table.ColumnHeader>Carry Over %</Table.ColumnHeader>
              <Table.ColumnHeader>Carry Over $</Table.ColumnHeader>
              <Table.ColumnHeader>Recurring</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {types.map((t) => (
              <Table.Row key={t.id}>
                <Table.Cell fontWeight="medium">{t.categoryType}</Table.Cell>
                <Table.Cell>
                  <Badge colorPalette={t.isGlobal ? "green" : "gray"} size="sm">
                    {t.isGlobal ? "Global" : "Provider"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{t.budgetPeriodType ?? "—"}</Table.Cell>
                <Table.Cell>{t.carryOverPercent != null ? `${t.carryOverPercent}%` : "—"}</Table.Cell>
                <Table.Cell>{t.carryOverAmount != null ? `$${t.carryOverAmount.toFixed(2)}` : "—"}</Table.Cell>
                <Table.Cell>
                  <Badge colorPalette={t.isReoccuring ? "green" : "gray"} size="sm">
                    {t.isReoccuring ? "Yes" : "No"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  {(!t.isGlobal || isSuperuser) && (
                    <Flex gap={1} justify="flex-end">
                      <TypeDialog
                        existing={t}
                        isSuperuser={isSuperuser}
                        trigger={<IconButton aria-label="Edit" size="xs" variant="ghost"><FiEdit2 /></IconButton>}
                      />
                      <IconButton
                        aria-label="Delete"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        loading={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(t.id)}
                      >
                        <FiTrash2 />
                      </IconButton>
                    </Flex>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {types.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={7} textAlign="center" color="gray.400" fontSize="sm">No funding types defined yet.</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  )
}

// ── Budget Categories ─────────────────────────────────────────────────────────

const CategoryDialog = ({
  existing,
  types,
  trigger,
}: {
  existing?: BudgetCategoryPublic
  types: BudgetCategoryTypePublic[]
  trigger: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      category: existing?.category ?? "",
      budgetCategoryTypeId: existing?.budgetCategoryTypeId ?? "",
    },
  })

  const mutation = useMutation({
    mutationFn: (data: { category: string; budgetCategoryTypeId: string }) =>
      existing
        ? BudgetCategorys.updateBudgetCategory({ path: { id: existing.id }, body: { category: data.category } })
        : BudgetCategorys.createBudgetCategory({ body: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetCategories"] })
      setOpen(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <DialogRoot
      open={open}
      onOpenChange={({ open: o }) => {
        setOpen(o)
        if (o) reset({
          category: existing?.category ?? "",
          budgetCategoryTypeId: existing?.budgetCategoryTypeId ?? "",
        })
      }}
      size={{ base: "xs", md: "sm" }}
      placement="center"
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{existing ? "Edit" : "Add"} Budget Category</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
          <DialogBody display="flex" flexDir="column" gap={3}>
            <Field.Root>
              <Field.Label fontSize="sm">Category Name</Field.Label>
              <Input size="sm" {...register("category", { required: true })} placeholder="e.g. Daily Activities" />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm">Funding Type</Field.Label>
              <select style={selectStyle} {...register("budgetCategoryTypeId", { required: true })} disabled={!!existing}>
                <option value="">— Select —</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.categoryType}</option>)}
              </select>
            </Field.Root>
          </DialogBody>
          <DialogFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} type="button">Cancel</Button>
            <Button size="sm" colorPalette="blue" type="submit" loading={mutation.isPending}>Save</Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

// ── Co-contribution defaults inline editor ────────────────────────────────────

const CoContributionDefaultsPanel = ({ category, providerId }: { category: BudgetCategoryPublic; providerId: string }) => {
  const queryClient = useQueryClient()
  const [editingPension, setEditingPension] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")

  const { data, isLoading } = useQuery({
    queryKey: ["coContributionDefaults", providerId, category.id],
    queryFn: () => CoContributionDefaults.listDefaults({ query: { providerId, budgetCategoryId: category.id } }),
    enabled: !!providerId,
  })

  const defaults = data?.data ?? []
  const getDefault = (pt: string) => defaults.find((d) => d.pensionType === pt)

  const upsertMutation = useMutation({
    mutationFn: (body: { pensionType: string; coContributionPercent: number }) =>
      CoContributionDefaults.upsertDefault({
        body: { providerId, budgetCategoryId: category.id, ...body },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coContributionDefaults", providerId, category.id] })
      queryClient.invalidateQueries({ queryKey: ["coContributionDefaults"] })
      setEditingPension(null)
    },
    onError: (err: ApiError) => handleError(err),
  })

  if (isLoading) return <Spinner size="xs" />

  return (
    <Box bg="gray.50" borderTop="1px solid" borderColor="gray.200" px={4} py={3}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide">
        Co-Contribution Defaults
      </Text>
      <Flex gap={6} wrap="wrap">
        {PENSION_TYPES.map((pt) => {
          const existing = getDefault(pt)
          const isEditing = editingPension === pt
          return (
            <Box key={pt} minW="160px">
              <Text fontSize="xs" color="gray.500" mb={1}>{pt}</Text>
              {isEditing ? (
                <Flex gap={2} align="center">
                  <Input
                    size="xs"
                    w="70px"
                    type="number"
                    step={0.1}
                    min={0}
                    max={100}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const pct = parseFloat(editValue)
                        if (!isNaN(pct)) upsertMutation.mutate({ pensionType: pt, coContributionPercent: pct })
                      }
                      if (e.key === "Escape") setEditingPension(null)
                    }}
                    autoFocus
                  />
                  <Button
                    size="xs"
                    colorPalette="blue"
                    loading={upsertMutation.isPending}
                    onClick={() => {
                      const pct = parseFloat(editValue)
                      if (!isNaN(pct)) upsertMutation.mutate({ pensionType: pt, coContributionPercent: pct })
                    }}
                  >
                    Save
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setEditingPension(null)}>✕</Button>
                </Flex>
              ) : (
                <Flex align="center" gap={2}>
                  <Badge colorPalette="gray" size="sm" minW="40px" textAlign="center">
                    {existing != null ? `${existing.coContributionPercent}%` : "—"}
                  </Badge>
                  <IconButton
                    aria-label="Edit"
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setEditValue(String(existing?.coContributionPercent ?? 0))
                      setEditingPension(pt)
                    }}
                  >
                    <FiEdit2 />
                  </IconButton>
                </Flex>
              )}
            </Box>
          )
        })}
      </Flex>
    </Box>
  )
}

const BudgetCategoriesTab = () => {
  const queryClient = useQueryClient()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""

  const { data: catData, isLoading: loadingCats } = useQuery({
    queryKey: ["budgetCategories"],
    queryFn: () => BudgetCategorys.readBudgetCategorys({ query: { limit: 500 } }),
  })
  const { data: typeData } = useQuery({
    queryKey: ["budgetCategoryTypes"],
    queryFn: () => BudgetCategoryTypes.listBudgetCategoryTypes({ query: { limit: 200 } }),
  })

  const categories = catData?.data ?? []
  const types = typeData?.data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => BudgetCategorys.deleteBudgetCategory({ path: { id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["budgetCategories"] }),
    onError: (err: ApiError) => handleError(err),
  })

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const byType = types.map((t) => ({
    type: t,
    cats: categories.filter((c) => c.budgetCategoryTypeId === t.id),
  })).filter((g) => g.cats.length > 0)
  const uncategorised = categories.filter((c) => !types.find((t) => t.id === c.budgetCategoryTypeId))
  if (uncategorised.length > 0) byType.push({ type: { id: "", categoryType: "Other", isGlobal: false }, cats: uncategorised })

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.500">Categories belong to a funding type. Click a row to set co-contribution defaults by pension type.</Text>
        <CategoryDialog
          types={types}
          trigger={<Button size="sm" colorPalette="blue"><FiPlus /> Add Category</Button>}
        />
      </Flex>
      {loadingCats && <Spinner size="sm" />}
      {!loadingCats && byType.map(({ type, cats }) => (
        <Box key={type.id} mb={6}>
          <Text fontWeight="semibold" fontSize="sm" color="blue.600" mb={2}>{type.categoryType}</Text>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
            {cats.map((c, idx) => {
              const expanded = expandedIds.has(c.id)
              return (
                <Box key={c.id} borderTop={idx > 0 ? "1px solid" : undefined} borderColor="gray.200">
                  <Flex
                    px={4}
                    py={2}
                    align="center"
                    gap={3}
                    _hover={{ bg: "gray.50" }}
                    cursor="pointer"
                    onClick={() => toggleExpand(c.id)}
                  >
                    <Box color="gray.400" flexShrink={0}>
                      {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    </Box>
                    <Text flex="1" fontWeight="medium" fontSize="sm">{c.category}</Text>
                    <Flex gap={1} onClick={(e) => e.stopPropagation()}>
                      <CategoryDialog
                        existing={c}
                        types={types}
                        trigger={<IconButton aria-label="Edit" size="xs" variant="ghost"><FiEdit2 /></IconButton>}
                      />
                      <IconButton
                        aria-label="Delete"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        loading={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(c.id)}
                      >
                        <FiTrash2 />
                      </IconButton>
                    </Flex>
                  </Flex>
                  {expanded && <CoContributionDefaultsPanel category={c} providerId={providerId} />}
                </Box>
              )
            })}
          </Box>
        </Box>
      ))}
      {!loadingCats && categories.length === 0 && (
        <Text color="gray.400" fontSize="sm">No categories defined yet.</Text>
      )}
    </Box>
  )
}

// ── Sub-Categories ────────────────────────────────────────────────────────────

const SubCategoryDialog = ({
  existing,
  categories,
  providerId,
  trigger,
}: {
  existing?: BudgetSubCategoryPublic
  categories: BudgetCategoryPublic[]
  providerId: string
  trigger: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: existing?.name ?? "",
      budgetCategoryId: existing?.budgetCategoryId ?? "",
      subCategoryType: existing?.subCategoryType ?? "service",
    },
  })

  const mutation = useMutation({
    mutationFn: (data: { name: string; budgetCategoryId: string; subCategoryType: string }) =>
      existing
        ? ServiceCatalog.updateSubcategory({
            path: { subCategoryId: existing.id },
            body: {
              name: data.name,
              budgetCategoryId: data.budgetCategoryId || null,
              subCategoryType: data.subCategoryType,
            },
          })
        : ServiceCatalog.createSubcategory({
            body: {
              providerId,
              name: data.name,
              budgetCategoryId: data.budgetCategoryId || null,
              subCategoryType: data.subCategoryType,
            },
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetSubCategories"] })
      setOpen(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <DialogRoot
      open={open}
      onOpenChange={({ open: o }) => {
        setOpen(o)
        if (o) reset({
          name: existing?.name ?? "",
          budgetCategoryId: existing?.budgetCategoryId ?? "",
          subCategoryType: existing?.subCategoryType ?? "service",
        })
      }}
      size={{ base: "xs", md: "sm" }}
      placement="center"
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{existing ? "Edit" : "Add"} Sub-Category</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
          <DialogBody display="flex" flexDir="column" gap={3}>
            <Field.Root>
              <Field.Label fontSize="sm">Name</Field.Label>
              <Input size="sm" {...register("name", { required: true })} placeholder="e.g. Personal Care" />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm">Budget Category</Field.Label>
              <select style={selectStyle} {...register("budgetCategoryId")}>
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.category}</option>
                ))}
              </select>
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm">Type</Field.Label>
              <select style={selectStyle} {...register("subCategoryType", { required: true })}>
                <option value="service">Service</option>
                <option value="purchase">Purchase</option>
              </select>
            </Field.Root>
          </DialogBody>
          <DialogFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} type="button">Cancel</Button>
            <Button size="sm" colorPalette="blue" type="submit" loading={mutation.isPending}>Save</Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

const BudgetSubCategoriesTab = () => {
  const queryClient = useQueryClient()
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""

  const { data: catData } = useQuery({
    queryKey: ["budgetCategories"],
    queryFn: () => BudgetCategorys.readBudgetCategorys({ query: { limit: 500 } }),
  })
  const categories: BudgetCategoryPublic[] = catData?.data ?? []

  const { data: subsData, isLoading } = useQuery({
    queryKey: ["budgetSubCategories", providerId],
    queryFn: () => ServiceCatalog.listSubcategoriesByProvider({ path: { providerId } }),
    enabled: !!providerId,
  })
  const subs: BudgetSubCategoryPublic[] = subsData?.data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ServiceCatalog.deleteSubcategory({ path: { subCategoryId: id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["budgetSubCategories"] }),
    onError: (err: ApiError) => handleError(err),
  })

  const catMap = new Map(categories.map((c) => [c.id, c.category ?? c.id]))

  // Group by budget category
  const grouped = categories.map((c) => ({
    cat: c,
    subs: subs.filter((s) => s.budgetCategoryId === c.id),
  })).filter((g) => g.subs.length > 0)
  const ungrouped = subs.filter((s) => !s.budgetCategoryId)

  if (!providerId) {
    return <Text color="red.500" fontSize="sm">No provider selected. Select a provider first.</Text>
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.500">
          Sub-categories belong to a budget category. Each sub-category is typed as either Service or Purchase.
        </Text>
        <SubCategoryDialog
          categories={categories}
          providerId={providerId}
          trigger={<Button size="sm" colorPalette="blue"><FiPlus /> Add Sub-Category</Button>}
        />
      </Flex>

      {isLoading && <Spinner size="sm" />}

      {!isLoading && (
        <Table.Root size="sm" variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Budget Category</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {/* Grouped by category */}
            {grouped.map(({ cat, subs: catSubs }) => (
              <>
                <Table.Row key={`header-${cat.id}`} bg="gray.50">
                  <Table.Cell colSpan={4} fontWeight="semibold" fontSize="xs" color="blue.600" textTransform="uppercase">
                    {cat.category}
                  </Table.Cell>
                </Table.Row>
                {catSubs.map((sub) => (
                  <Table.Row key={sub.id}>
                    <Table.Cell fontWeight="medium">{sub.name}</Table.Cell>
                    <Table.Cell fontSize="xs" color="gray.500">{catMap.get(sub.budgetCategoryId ?? "") ?? "—"}</Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={sub.subCategoryType === "purchase" ? "orange" : "blue"}
                        size="sm"
                      >
                        {sub.subCategoryType === "purchase" ? "Purchase" : "Service"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap={1} justify="flex-end">
                        <SubCategoryDialog
                          existing={sub}
                          categories={categories}
                          providerId={providerId}
                          trigger={<IconButton aria-label="Edit" size="xs" variant="ghost"><FiEdit2 /></IconButton>}
                        />
                        <IconButton
                          aria-label="Delete"
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(sub.id)}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </>
            ))}
            {/* Ungrouped */}
            {ungrouped.length > 0 && (
              <>
                <Table.Row bg="gray.50">
                  <Table.Cell colSpan={4} fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase">
                    No Category
                  </Table.Cell>
                </Table.Row>
                {ungrouped.map((sub) => (
                  <Table.Row key={sub.id}>
                    <Table.Cell fontWeight="medium">{sub.name}</Table.Cell>
                    <Table.Cell fontSize="xs" color="gray.500">—</Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={sub.subCategoryType === "purchase" ? "orange" : "blue"}
                        size="sm"
                      >
                        {sub.subCategoryType === "purchase" ? "Purchase" : "Service"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap={1} justify="flex-end">
                        <SubCategoryDialog
                          existing={sub}
                          categories={categories}
                          providerId={providerId}
                          trigger={<IconButton aria-label="Edit" size="xs" variant="ghost"><FiEdit2 /></IconButton>}
                        />
                        <IconButton
                          aria-label="Delete"
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(sub.id)}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </>
            )}
            {subs.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center" color="gray.400" fontSize="sm">No sub-categories defined yet.</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

function BudgetCategoriesPage() {
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12} mb={1}>Budget Categories</Heading>
      <Text color="gray.500" mb={6} fontSize="sm">
        Manage funding types, categories, and sub-categories.
      </Text>

      <Tabs.Root defaultValue="types" variant="line">
        <Tabs.List mb={4}>
          <Tabs.Trigger value="types">Funding Types</Tabs.Trigger>
          <Tabs.Trigger value="categories">Categories</Tabs.Trigger>
          <Tabs.Trigger value="subcategories">Sub-Categories</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="types">
          <BudgetCategoryTypesTab />
        </Tabs.Content>
        <Tabs.Content value="categories">
          <BudgetCategoriesTab />
        </Tabs.Content>
        <Tabs.Content value="subcategories">
          <BudgetSubCategoriesTab />
        </Tabs.Content>
      </Tabs.Root>
    </Container>
  )
}
